import React, { lazy, Suspense, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import AppNav from "./components/appNav";
import Loader from "./components/loader";
import Footer from "./components/footer/footer";
import SignupRoutes from "./pages/Signup/layout/signupRoutes";
import ErrorPage from "./pages/errorPage/errorPage";
import PageTitle from "./components/title";
import ScrollToTop from "./components/scrollToTop";
import AuthForbid from "./components/authForbid";
import { useMeMutation } from "./service/userApi";
import { useDispatch, useSelector } from "react-redux";
import { setUserType, selectIsAuthenticated } from "./features/authSlice";
import ErrorBoundaryWrapper from "./components/errorBoundary";
import GTMPageViewTracker from "./components/gtmPageViewTracker";
import FacebookPixel from "./components/facebookPixel";
import { AuthModalProvider } from "./helpers/authmodalContext";

// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }
}
const ApprenticeDetailsPage = lazy(
  () => import("./pages/apprenticeDetails/apprenticeDetailsPage")
);
const ApprenticePage = lazy(() => import("./pages/apprentice/apprenticePage"));
const BlogDetailPage = lazy(() => import("./pages/blogDetails/blogDetailPage"));
const HomePage = lazy(() => import("./pages/home/homepage"));
const Contactus = lazy(() => import("./pages/Contactus/Contactus"));
const JobRequests = lazy(() => import("./pages/JobRequests/JobRequests"));
const TradiesPage = lazy(() => import("./pages/tradies/tradiesPage"));
const JobRequestsDetailsPage = lazy(
  () => import("./pages/JobRequestDetails/jobRequestsDetaisPage")
);
const UserJobDetailsPage = lazy(
  () => import("./pages/userJobDetails/userJobDetailsPage")
);
const Login = lazy(() => import("./pages/Login/Login"));
const BlogsPage = lazy(() => import("./pages/blogs/blogsPage"));
const TradieDetails = lazy(() => import("./pages/tradieDetails/tradieDetails"));
const ServicesPage = lazy(() => import("./pages/services/servicesPage"));
const ServiceDetails = lazy(
  () => import("./pages/servicesDetails/serviceDetails")
);
const Notifications = lazy(() => import("./pages/notifications/Notifications"));
const ChatLayout = lazy(() => import("./pages/chats/ChatLayout"));
const ContactWithClient = lazy(
  () => import("./pages/ContactWithClient/ContactWithClient")
);
const SettingsRoutes = lazy(
  () => import("./pages/settings/layout/settingsRoutes")
);
const PostJob = lazy(() => import("./pages/postJob/postJob"));
const UserProfile = lazy(() => import("./pages/profile/profile"));
const VerifyUser = lazy(() => import("./pages/verifyUser/VerifyUser"));
const AuthGuard = lazy(() => import("./components/authGuard"));
const TermsOfUse = lazy(() => import("./pages/termsOfUse/termsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/privacyPolicy/privacyPolicy"));
const UserJobHistory = lazy(
  () => import("./pages/userJobHistory/userJobHistory")
);

function App() {
  const [me, { isLoading }] = useMeMutation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const hasFetchedUser = useRef(false);

  useEffect(() => {
    const fetchUser = async () => {
      // Only fetch once per mount when authenticated
      if (isAuthenticated && !hasFetchedUser.current) {
        hasFetchedUser.current = true;
        try {
          const data = await me("").unwrap();
          dispatch(
            setUserType({
              userType: data.data.userType,
              userVerified: data.data.userVerified,
              firstName: data.data.firstName,
              lastName: data.data.lastName,
              avatar: data.data.avatar,
            })
          );
        } catch (error) {
          // 401 errors are handled globally by apiSlice
          console.error("Failed to fetch user data:", error);
        }
      }
    };

    fetchUser();
  }, [dispatch, me, isAuthenticated]);

  useEffect(() => {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({
      event: "page_loaded",
      pagePath: window.location.pathname,
    });
  }, []);

  if (isLoading)
    return (
      <div className="h-screen flex justify-center items-center">
        {" "}
        <Loader />{" "}
      </div>
    );
  return (
    <>
      <BrowserRouter>
        <AuthModalProvider>
          <GTMPageViewTracker />
          <FacebookPixel />
          <ErrorBoundaryWrapper>
            <AppNav />
            <Suspense
              fallback={
                <div className="w-full h-screen flex justify-center items-center ">
                  <Loader />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<HomePage />}></Route>
                <Route
                  path="userProfile"
                  element={
                    <AuthGuard>
                      <PageTitle title="Profile" />
                      <UserProfile />
                    </AuthGuard>
                  }
                ></Route>
                <Route
                  path="userJobHistory"
                  element={
                    <AuthGuard>
                      <PageTitle title="jobHistory" />
                      <UserJobHistory />
                    </AuthGuard>
                  }
                />
                <Route
                  path="userJobHistory"
                  element={
                    <AuthGuard>
                      <PageTitle title="jobHistory" />
                      <UserJobHistory />
                    </AuthGuard>
                  }
                />
                <Route
                  path="userJobHistory/:id"
                  element={
                    <AuthGuard>
                      <PageTitle title="jobHistory" />
                      <UserJobDetailsPage />
                    </AuthGuard>
                  }
                />
                {/* <Route
                  path="userSubscription"
                  element={
                    <>
                      <AuthGuard>
                        <PageTitle title="subscription Details" />
                        <SubscriptionDetailsPage />
                      </AuthGuard>
                    </>
                  }
                ></Route> */}
                <Route
                  path="postJob"
                  element={
                    <>
                      <AuthGuard
                        allowedRoles={["client"]}
                        requiredVerification={true}
                      >
                        <PageTitle title="Post Job" />
                        <PostJob />
                      </AuthGuard>
                    </>
                  }
                />
                <Route
                  path="jobRequests"
                  element={
                    <>
                      {" "}
                      <AuthGuard allowedRoles={["tradie", "freeTradie"]}>
                        <PageTitle title="Job Requests" />
                        <JobRequests />{" "}
                      </AuthGuard>
                    </>
                  }
                />
                <Route
                  path="jobRequest/:id"
                  element={
                    <AuthGuard allowedRoles={["tradie"]}>
                      <PageTitle title="Job Request" />
                      <JobRequestsDetailsPage />{" "}
                    </AuthGuard>
                  }
                />
                <Route
                  path="professional"
                  element={
                    <>
                      <AuthGuard
                        allowedRoles={["apprentice", "tradie", "freeTradie"]}
                        requiredVerification={true}
                      >
                        <PageTitle title="Professionals" />
                        <TradiesPage />
                      </AuthGuard>
                    </>
                  }
                />
                <Route
                  path="professional/:id"
                  element={
                    <AuthGuard
                      allowedRoles={["apprentice", "tradie", "freeTradie"]}
                      requiredVerification={true}
                    >
                      <PageTitle title="Professional" />
                      <TradieDetails />{" "}
                    </AuthGuard>
                  }
                />
                <Route
                  path="contactUs"
                  element={
                    <>
                      <PageTitle title="Contact Us" />
                      <Contactus />
                    </>
                  }
                ></Route>
                <Route
                  path="login"
                  element={
                    <AuthForbid>
                      <PageTitle title="Login" />
                      <Login />
                    </AuthForbid>
                  }
                ></Route>
                <Route
                  path="signup/*"
                  element={
                    <AuthForbid>
                      <PageTitle title="Signup" />
                      <SignupRoutes />
                    </AuthForbid>
                  }
                ></Route>
                <Route path="user/verify" element={<VerifyUser />} />
                <Route
                  path="service"
                  element={
                    <>
                      <PageTitle title="Services" />
                      <ServicesPage />
                    </>
                  }
                />
                <Route
                  path="service/:id"
                  element={
                    <AuthGuard allowedRoles={["tradie"]}>
                      <PageTitle title="Service Details" />
                      <ServiceDetails />
                    </AuthGuard>
                  }
                />
                <Route
                  path="blogs"
                  element={
                    <>
                      <PageTitle title="Blogs" />
                      <BlogsPage />
                    </>
                  }
                />
                <Route
                  path="notifications"
                  element={
                    <>
                      <AuthGuard>
                        <PageTitle title="Notifications" />
                        <Notifications />
                      </AuthGuard>
                    </>
                  }
                />
                <Route
                  path="chats"
                  element={
                    <>
                      <AuthGuard>
                        <PageTitle title="Chats" />
                        <ChatLayout />
                      </AuthGuard>
                    </>
                  }
                />
                <Route
                  path="chats/:id"
                  element={
                    <>
                      <AuthGuard>
                        <PageTitle title="Chat Room" />
                        <ChatLayout />
                      </AuthGuard>
                    </>
                  }
                />
                <Route
                  path="termsOfUse"
                  element={
                    <>
                      <PageTitle title="Terms Of Use" />
                      <TermsOfUse />
                    </>
                  }
                />
                <Route
                  path="privacyPolicy"
                  element={
                    <>
                      <PageTitle title="Privacy Policy" />
                      <PrivacyPolicy />
                    </>
                  }
                />
                <Route
                  path="contactWithclient"
                  element={
                    <AuthGuard>
                      <ContactWithClient />
                    </AuthGuard>
                  }
                />
                <Route
                  path="settings/*"
                  element={
                    <>
                      {" "}
                      <AuthGuard>
                        {" "}
                        <PageTitle title="Settings" />
                        <SettingsRoutes />
                      </AuthGuard>{" "}
                    </>
                  }
                />
                <Route
                  path="blogs/:id"
                  element={
                    <>
                      <PageTitle title="Blog Detail" /> <BlogDetailPage />
                    </>
                  }
                />
                <Route
                  path="apprentice"
                  element={
                    <AuthGuard
                      allowedRoles={["tradie", "freeTradie"]}
                      requiredVerification={true}
                    >
                      <PageTitle title="Apprentice" />
                      <ApprenticePage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="apprentice/:id"
                  element={
                    <AuthGuard
                      allowedRoles={["tradie", "freeTradie"]}
                      requiredVerification={true}
                    >
                      <PageTitle title="Apprentice Details" />
                      <ApprenticeDetailsPage />
                    </AuthGuard>
                  }
                />
                <Route path="*" element={<ErrorPage />} />
              </Routes>
            </Suspense>
            <Footer />
            <ScrollToTop />
          </ErrorBoundaryWrapper>
        </AuthModalProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
